import React, { useContext, Fragment } from 'react'
import { Separator } from '@fs/zion-ui'
import { usePageView } from '@fs/zion-analytics'
import { css } from '@emotion/core'
import { useQuery } from '@apollo/client'
import { useLocation, useRouteMatch, Link, useParams } from '@fs/zion-router'
import { useTranslation } from 'react-i18next'
import Head, { Title } from '@fs/zion-head'
import { boolAttr } from '@fs/zion-frontend-friends'
import TopDetailBar from '../TopDetailBar'
import api from '../../api/brightspot'
import DesktopHeaderWithVideo from '../DesktopHeaderWithVideo'
import DownloadableResources from '../DownloadableResources'
import PillButton from '../PillButton'
import SectionHeader from '../SectionHeader'
import SocialRow from '../social/SocialRow'
import SocialButton from '../social/SocialButton'
import RichTextDisplayer from '../RichTextDisplayer'
import getSocialsFromBrightspot from '../../helpers/BrightSpotSocialHelper'
import SpotlightGradient from '../SpotlightGradient'
import DetailsPageContainer from '../DetailsPageContainer'
import LargeLoadingSpinner from '../LargeLoadingSpinner'
import useIsMobile from '../../hooks/useIsMobile'
import ContentCardRow from '../ContentCardRow'
import { useCurrentVendor } from '../VendorProvider'
import { useChatOverlayContext } from '../chat/ChatOverlayProvider'
import VideoSwitcher from '../VideoSwitcher'
import ancestryImage from '../../images/ancestry-hero.png'
import { markItemsInPlaylist } from '../../global/playlistHelper'
import { PlaylistContext } from '../PlaylistContext'
import { Chat } from '../../global/chatSettingsConfig'

const sponsorPrimaryImageCss = css`
  border-radius: 50%;
  object-fit: contain;
  max-width: 160px;
  max-height: 160px;
  margin-right: 3vw;
  padding: 10px;
  background-color: #ffffff;
`

const overlineCss = css`
  font-size: 20px;
  font-weight: 700;
  text-transform: uppercase;
`

const detailsSectionCss = css`
  display: flex;
  align-items: center;
`

const headingCss = css`
  font-size: 56px;
  font-weight: 700;
  line-height: 64px;
  &[is-mobile] {
    font-size: 32px;
    line-height: 44px;
  }
`

const platinumGradientFlavors = ['ancestry', 'familysearch', 'myheritage']

const addDownloadableIfExists = (data) => {
  const downloadables = []
  const potentialDownloadables = [
    'productBrochurePdf1',
    'productBrochurePdf2',
    'productBrochurePdf3',
    'productBrochurePdf4',
    'productBrochurePdf5',
  ]

  potentialDownloadables.forEach((download, ind) => {
    const downloadable = data?.ExhibitorPage?.[download]?.href
      ? {
          title: data.ExhibitorPage?.[`productBrochureTitle${ind + 1}`],
          href: data.ExhibitorPage[download].href,
        }
      : null

    if (downloadable) {
      downloadables.push(downloadable)
    }
  })

  return downloadables
}

const VirtualBoothPage = ({ previewId }) => {
  const [t] = useTranslation()
  const location = useLocation()
  const routeMatch = useRouteMatch('/expohall/:id')
  const isMobile = useIsMobile()
  const { id: sponsorId } = useParams()
  const pathOrId = previewId || `/expohall/${routeMatch?.params.id}`
  const { data, loading } = useQuery(api.virtualBooth.virtualBoothGQLQuery(pathOrId), {
    // Don't request the data if we aren't on the right route
    skip: !pathOrId,
  })

  const { playlist } = useContext(PlaylistContext)

  usePageView({ page_detail: `${location.pathname}` })

  const { setIsOpen, isOpen } = useChatOverlayContext()

  const { data: vendorQueryData } = useCurrentVendor()
  const vendorData = vendorQueryData?.ExhibitorPage
  const dataRoot = data?.ExhibitorPage
  const sponsorUrl = dataRoot?.websiteUrl?.url
  const companyDescription = dataRoot?.description
  const companyContactUsUrl = dataRoot?.companyContactUsUrl?.url
  const companySupportUrl = dataRoot?.companySupportUrl?.url
  const contactEmail = dataRoot?.contactEmail
  const contentId = dataRoot?.contentId
  const headingText = dataRoot?.name
  const image = dataRoot?.logo?.custom?.image?.src
  const videoDetails = dataRoot?.welcomeVideo
  let fallbackImage = dataRoot?.graphics?.[0]?.custom?.image?.src

  if (sponsorId === 'ancestry') fallbackImage = ancestryImage

  const socialPlatforms = getSocialsFromBrightspot(
    ['linkedin', 'instagram', 'facebook', 'youtube', 'twitter'],
    data?.ExhibitorPage
  )
  const downloadableResources = addDownloadableIfExists(data)
  const faqsUrl = dataRoot?.faqsUrl?.url

  const promotions = data?.ExhibitorPage?.promotions
  const discoverMore = markItemsInPlaylist({ playlist, items: dataRoot?.additionalExperiences })

  const companyDescriptionStyle = css`
    word-wrap: break-word;
    overflow-wrap: anywhere;
  `
  const fallbackImageStyle = css`
    object-fit: cover;
    width: 100%;
  `

  const openChat = () => {
    if (!isOpen) {
      setIsOpen(true)
    }
    window.chatHistory.push(`/ask-an-expert/${vendorData?.name}/${contentId}`)
  }

  const actions = (
    <div>
      {Chat.Settings.ASK_EXPERT_ENABLED && (
        <>
          <PillButton
            minWidth
            data-testid="chat-with-an-expert"
            emphasis={isMobile ? 'high' : 'medium'}
            onClick={!companySupportUrl ? openChat : null}
            to={companySupportUrl}
          >
            {t('virtual-booth.chat-with-expert', 'Chat with an expert')}
          </PillButton>
          <Separator size="sm" />
        </>
      )}
      {faqsUrl && (
        <PillButton minWidth data-testid="company-faq" to={faqsUrl} emphasis={isMobile ? 'high' : 'medium'}>
          {t('virtual-booth.company-faq', 'Company FAQ')}
        </PillButton>
      )}
    </div>
  )

  return (
    <>
      <Title breadcrumbs={[t('site-title', 'RootsTech 2021')]}>{vendorData?.name}</Title>

      <SpotlightGradient
        flavor={platinumGradientFlavors.includes(sponsorId) ? sponsorId : 'mystery'}
        size={isMobile ? 'sm' : 'md'}
      />

      {loading ? (
        <LargeLoadingSpinner />
      ) : (
        <DetailsPageContainer>
          <Head>
            <meta property="og:url" content={window.location.href} />
            <meta property="og:type" content="video" />
            <meta
              property="og:title"
              content={`${t('virtual-booth.label', 'Virtual Booth')} \u2022 ${headingText} \u2022 ${t(
                'site-title',
                'RootsTech 2021'
              )} \u2022 FamilySearch `}
            />
            {/* We could maybe strip out the html in the description in the future. */}
            {/* <meta property="og:description" content={companyDescription} /> */}
            <meta property="og:image" content={image} />
          </Head>

          <TopDetailBar backText={t('common-ui:back.action', 'Back')} backLink="/expohall" />
          {!isMobile && <Separator size="sm" />}

          <DesktopHeaderWithVideo
            noShadow={sponsorId === 'ancestry'}
            video={
              videoDetails && sponsorId !== 'ancestry' ? (
                <VideoSwitcher videoProvider={videoDetails?.videoProvider} smallBrightcoveSize noMinHeight />
              ) : (
                <img src={fallbackImage} css={fallbackImageStyle} alt="" aria-hidden />
              )
            }
          >
            {isMobile && <Separator size="lg" />}
            <div css={detailsSectionCss}>
              <img
                src={image}
                alt=""
                aria-hidden
                css={sponsorPrimaryImageCss}
                style={{ width: isMobile ? 80 : 160, height: isMobile ? 80 : 160 }}
              />
              {!isMobile && actions}
            </div>

            <Separator />

            {!isMobile && <div css={overlineCss}>{t('virtual-booth.label', 'Virtual Booth')}</div>}

            <h1 css={headingCss} is-mobile={boolAttr(isMobile)}>
              {headingText}
            </h1>

            <Separator size={isMobile ? 'sm' : 'xs'} />

            <div css={companyDescriptionStyle}>
              <RichTextDisplayer richText={companyDescription} />
            </div>

            {isMobile && (
              <>
                <Separator size="md" />
                {actions}
              </>
            )}
          </DesktopHeaderWithVideo>
          <Separator size="xl" />
          {data?.ExhibitorPage?.content?.map((section) => (
            <Fragment key={section.title}>
              <ContentCardRow headingText={section.title} items={section?.items} useChildLimiter isPlaylistable />
              <Separator size="xl" />
            </Fragment>
          ))}

          {promotions?.length > 0 && (
            <>
              <ContentCardRow
                headingText={t('virtual-booth.promotions', 'Promotions')}
                items={promotions}
                useChildLimiter
              />
              <Separator size="xl" />
            </>
          )}

          {discoverMore?.length > 0 && (
            <>
              <ContentCardRow
                headingText={t('virtual-booth.discover-more', 'Discover more')}
                items={discoverMore}
                useChildLimiter
                isPlaylistable
              />
              <Separator size="xl" />
            </>
          )}

          {downloadableResources?.length > 0 && (
            <>
              <DownloadableResources downloadableResources={downloadableResources ?? []} />
              <Separator size="xl" />
            </>
          )}
          <SectionHeader
            heading={t(`virtual-booth.connect-with-${sponsorId}`, 'Connect with {name}', { name: headingText })}
            small={isMobile}
          />
          {(companyContactUsUrl || contactEmail) && (
            <PillButton
              minWidth
              external
              data-testid="contact-us"
              to={companyContactUsUrl ?? `mailto:${contactEmail}`}
              emphasis={isMobile ? 'high' : 'medium'}
            >
              {t('virtual-booth.contact-us', 'Contact Us')}
            </PillButton>
          )}

          <Separator size="sm" />
          <Link data-testid="sponsor-url" to={sponsorUrl}>
            {sponsorUrl}
          </Link>
          <Separator size="sm" />
          <SocialRow isMobile={isMobile}>
            {socialPlatforms?.map((platform) => (
              <SocialButton
                isMobile={isMobile}
                key={platform.platform}
                platform={platform.platform}
                to={platform.url}
                external
              />
            ))}
          </SocialRow>
          <Separator size="xxl" />
        </DetailsPageContainer>
      )}
    </>
  )
}

export default VirtualBoothPage
